import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon, MoreIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import { DragDropContext, Droppable, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import styled from 'styled-components';

import FormEditCaseCommentTemplate, {
  FormEditCaseCommentTemplateData,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/FormEditCaseCommentTemplate';
import DraggableListItem from 'dashboard/components/DraggableListItem';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Tooltip from 'dashboard/components/Tooltip';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { CaseCommentTemplate } from 'dashboard/models/case-template';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

const SettingsContainer = styled('div')`
  padding: ${Spacing[5]};
`;

const CommentTemplateHeader = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: 2fr 1fr;
  align-items: center;
`;

const StyledTableTR = styled(TableTR)`
  width: 100%;
`;

const StyledTableTD = styled(TableTD)`
  width: 100%;
  border-bottom: 0;
`;

interface CaseCommentPanelInterface {
  caseCommentTemplates: CaseCommentTemplate[];
  addCommentTemplate: () => void;
  updateCommentTemplate: (updateCommentTemplate: CaseCommentTemplate) => void;
  deleteCommentTemplate: (token: string) => void;
  reorderCommentTemplateList: (sourceIndex: number, destinationIndex: number) => void;
}

const CaseCommentPanel = ({
  caseCommentTemplates,
  addCommentTemplate,
  updateCommentTemplate,
  deleteCommentTemplate,
  reorderCommentTemplateList,
}: CaseCommentPanelInterface) => {
  const [currentCommentTemplate, setCurrentCommentTemplate] = useState<CaseCommentTemplate>();
  const [
    editCommentTemplateModal,
    { enable: showEditCommentTemplateModal, disable: hideEditCommentTemplateModal },
  ] = useToggle(false);
  const moreItems = (commentTemplate: CaseCommentTemplate) => {
    return [
      {
        id: 'case-comment-panel-templates__edit_button',
        label: 'Edit',
        variant: 'basic' as const,
        onClick: () => {
          setCurrentCommentTemplate(commentTemplate);
          showEditCommentTemplateModal();
        },
      },
      {
        id: 'case-comment-panel-templates__delete_button',
        label: 'Delete',
        variant: 'danger' as const,
        onClick: () => deleteCommentTemplate(commentTemplate.token),
      },
    ];
  };

  const handleUpdateCommentTemplate = (commentTemplateData: FormEditCaseCommentTemplateData) => {
    updateCommentTemplate(commentTemplateData);
    hideEditCommentTemplateModal();
  };

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    reorderCommentTemplateList(source.index, destination.index);
  };

  const caseCommentTemplateRows = caseCommentTemplates.map((commentTemplate, idx) => {
    return (
      <DraggableListItem
        key={commentTemplate.token}
        draggableId={commentTemplate.token}
        index={idx}
      >
        <StyledTableTR key={commentTemplate.token}>
          <StyledTableTD>{commentTemplate.name}</StyledTableTD>
          <StyledTableTD>
            <DropdownMenu
              trigger={
                <Button
                  id='case-comment-panel-row__dropdown'
                  variant='inverse'
                  width='icon'
                  aria-label='More'
                >
                  <MoreIcon />
                </Button>
              }
              align='end'
            >
              {moreItems(commentTemplate).map((item) => (
                <DropdownMenu.Item
                  id={item.id}
                  onClick={item.onClick}
                  key={item.id}
                  variant={item.variant}
                >
                  {item.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
          </StyledTableTD>
        </StyledTableTR>
      </DraggableListItem>
    );
  });

  return (
    <>
      <SettingsContainer>
        <CommentTemplateHeader>
          <Heading size='sm'>
            Preset comments&nbsp;
            <Tooltip
              trigger='hover'
              tooltip={
                <Paragraph color='primary' size='sm'>
                  Use preset comments when updating the case status or using the comments module.
                </Paragraph>
              }
              hideArrow={true}
              placement='top-start'
            >
              <CircleInfoIcon />
            </Tooltip>
          </Heading>
          <Button
            id='case-comment-panel__add-comment-template'
            variant='inverse'
            prefixIcon={<PlusIcon />}
            width='auto'
            onClick={addCommentTemplate}
          >
            Add
          </Button>
        </CommentTemplateHeader>
        <Spacer height={1} />
        <TableLayout
          showHeader={caseCommentTemplates.length > 0}
          isEmpty={caseCommentTemplates.length === 0}
          nullState={<></>}
          headerTR={
            <>
              <TableTH>Comments</TableTH>
            </>
          }
        >
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='checklist-item-panel-item-table__droppable-selected'>
              {(provided) => {
                return (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {caseCommentTemplateRows}
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
        </TableLayout>
      </SettingsContainer>
      <ModalWrapper
        isOpen={editCommentTemplateModal}
        handleCloseModal={hideEditCommentTemplateModal}
      >
        <ModalContainer>
          <ModalHeader onClose={hideEditCommentTemplateModal}>
            Edit {currentCommentTemplate?.name}
          </ModalHeader>
          <ModalBody>
            <FormEditCaseCommentTemplate
              defaultValues={{
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                name: currentCommentTemplate?.name,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                token: currentCommentTemplate?.token,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                body: currentCommentTemplate?.body,
              }}
              handleClose={hideEditCommentTemplateModal}
              handleUpdateCommentTemplate={handleUpdateCommentTemplate}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

export default CaseCommentPanel;
